import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Download, Upload, AlertCircle, CheckCircle, X, ChevronRight } from 'lucide-react';
import { API_URL } from '../../config';

export default function ImportBrandModal({ isOpen, onClose, onImportSuccess, system = 'geo' }) {
  const [step, setStep] = useState(1);
  const [parsedData, setParsedData] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [summary, setSummary] = useState(null);
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  if (!isOpen) return null;

  const resetState = () => {
    setStep(1);
    setParsedData([]);
    setError(null);
    setSummary(null);
    setIsSubmitting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const processExcelData = (workbook) => {
    try {
      const projectsMap = new Map(); // brandName -> projectObject

      if (system === 'seo') {
        const ws1 = workbook.Sheets['基础设置'];
        if (ws1) {
          const data1 = XLSX.utils.sheet_to_json(ws1);
          data1.forEach(row => {
            const brandName = row['品牌名称(必填)']?.toString().trim();
            if (!brandName || brandName.includes('示例品牌')) return;
            
            if (!projectsMap.has(brandName)) {
              projectsMap.set(brandName, { 
                name: brandName, 
                industry: row['所属行业'] || '',
                targetAudience: row['目标受众'] || '',
                competitors: [], 
                seoKeywords: [] 
              });
            }
            
            ['核心竞品1(选填)', '核心竞品2(选填)', '核心竞品3(选填)'].forEach(key => {
              if (row[key]) {
                projectsMap.get(brandName).competitors.push({ name: row[key], aliases: '' });
              }
            });
          });
        }
        const ws2 = workbook.Sheets['SEO词库配置'];
        if (ws2) {
          const data2 = XLSX.utils.sheet_to_json(ws2);
          data2.forEach(row => {
            const brandName = row['所属品牌名称(需与基础设置一致)']?.toString().trim();
            if (!brandName || brandName.includes('示例品牌')) return;
            
            if (!projectsMap.has(brandName)) {
              projectsMap.set(brandName, { name: brandName, competitors: [], seoKeywords: [] });
            }
            
            if (row['SEO关键词(必填)']) {
              projectsMap.get(brandName).seoKeywords.push({
                keyword: row['SEO关键词(必填)'],
                category: row['词类/分组(如:核心业务词/长尾词)'] || '核心业务词',
                searchVolume: row['预估搜索量(选填)'] ? parseInt(row['预估搜索量(选填)']) : 0,
                difficulty: row['优化难度(选填,1-100)'] ? parseInt(row['优化难度(选填,1-100)']) : 50
              });
            }
          });
        }
      } else {
        // Original GEO parsing logic
        const ws1 = workbook.Sheets['品牌与受众人设'] || workbook.Sheets[workbook.SheetNames[0]];
        const data1 = XLSX.utils.sheet_to_json(ws1);
        data1.forEach(row => {
          const brandName = row['归属品牌名称']?.toString().trim();
          if (!brandName || brandName.includes('可删除')) return;
          if (!projectsMap.has(brandName)) {
            projectsMap.set(brandName, { name: brandName, keywords: row['品牌核心词'] || '', personas: [], competitors: [], themes: [], classifiedKeywords: [] });
          }
          if (row['受众人设名称']) {
            projectsMap.get(brandName).personas.push({
              name: row['受众人设名称'],
              prompt: row['人设背景描述(提示词)'] || row['人设背景描述'] || ''
            });
          }
        });

        const ws2 = workbook.Sheets['竞品矩阵'];
        if (ws2) {
          const data2 = XLSX.utils.sheet_to_json(ws2);
          data2.forEach(row => {
            const brandName = row['归属品牌名称']?.toString().trim();
            if (!brandName || brandName.includes('可删除')) return;
            if (!projectsMap.has(brandName)) {
              projectsMap.set(brandName, { name: brandName, personas: [], competitors: [], themes: [], classifiedKeywords: [] });
            }
            if (row['竞品名称']) {
              projectsMap.get(brandName).competitors.push({
                name: row['竞品名称'],
                aliases: row['竞品核心词'] || ''
              });
            }
          });
        }

        const ws3 = workbook.Sheets['业务维度'];
        if (ws3) {
          const data3 = XLSX.utils.sheet_to_json(ws3);
          const themesMap = new Map();
          data3.forEach(row => {
            const brandName = row['归属品牌名称']?.toString().trim();
            if (!brandName || brandName.includes('可删除')) return;
            if (!projectsMap.has(brandName)) {
              projectsMap.set(brandName, { name: brandName, personas: [], competitors: [], themes: [], classifiedKeywords: [] });
            }
            const l1 = row['一级业务维度']?.toString().trim();
            const l2 = row['二级细分维度']?.toString().trim();
            const kws = row['包含的关键词(逗号分隔)'] || row['包含的关键词'] || '';
            if (l1) {
              const themeKey = brandName + '_' + l1;
              if (!themesMap.has(themeKey)) {
                const newTheme = { name: l1, keywords: !l2 ? kws : '', subThemes: [] };
                themesMap.set(themeKey, newTheme);
                projectsMap.get(brandName).themes.push(newTheme);
              }
              if (l2) {
                themesMap.get(themeKey).subThemes.push({
                  name: l2,
                  keywords: kws
                });
              }
            }
          });
        }
        const ws4 = workbook.Sheets['抓取词库配置'];
        if (ws4) {
          const data4 = XLSX.utils.sheet_to_json(ws4);
          data4.forEach(row => {
            const brandName = row['归属品牌名称']?.toString().trim();
            if (!brandName || brandName.includes('可删除')) return;
            if (!projectsMap.has(brandName)) {
              projectsMap.set(brandName, { name: brandName, personas: [], competitors: [], themes: [], classifiedKeywords: [] });
            }
            
            const pkgName = row['词包名称']?.toString().trim() || '未分类';
            const word = row['抓取搜索词(指令)']?.toString().trim();
            if (!word) return;

            const category = row['词性(Brand/Competitor/Generic)']?.toString().trim() || '';
            const volumeStr = row['搜索热度(高/中/低)']?.toString().trim() || '';
            const stage = row['4A阶段(Aware/Appeal/Ask/Act)']?.toString().trim() || '';
            const macro = row['宏观业务维度']?.toString().trim() || '';
            const micro = row['微观业务维度']?.toString().trim() || '';

            const project = projectsMap.get(brandName);
            if (!project.classifiedKeywords) project.classifiedKeywords = [];
            
            let pkg = project.classifiedKeywords.find(p => p.name === pkgName);
            if (!pkg) {
              pkg = { id: Date.now() + Math.random(), name: pkgName, items: [] };
              project.classifiedKeywords.push(pkg);
            }
            
            pkg.items.push({
              id: Date.now() + Math.random(),
              expansions: word,
              category: category,
              volume: volumeStr,
              stage: stage,
              macro: macro,
              micro: micro
            });
          });
        }

      }

      const projects = Array.from(projectsMap.values());
      
      if (projects.length === 0) {
        setError('未在表格中找到有效的品牌数据，请检查必填项。');
        return;
      }

      
      let totalCompetitors = 0;
      let totalPersonas = 0;
      let totalThemes = 0;
      let totalSeoKws = 0;
      let uniqueSeoGroups = new Set();
      
      projects.forEach(p => {
        totalCompetitors += (p.competitors?.length || 0);
        totalPersonas += (p.personas?.length || 0);
        totalThemes += (p.themes?.length || 0) + (p.themes || []).reduce((acc, t) => acc + (t.subThemes?.length || 0), 0);
        totalSeoKws += (p.seoKeywords?.length || 0);
        if (p.seoKeywords) {
          p.seoKeywords.forEach(kw => {
             if (kw.category) uniqueSeoGroups.add(p.name + '_' + kw.category);
          });
        }
      });

      setSummary({
        brands: projects.length,
        competitors: totalCompetitors,
        personas: totalPersonas,
        themes: totalThemes,
        seoKeywords: totalSeoKws,
        seoGroups: uniqueSeoGroups.size
      });


      setParsedData(projects);
      setStep(2);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('解析 Excel 文件失败，请确保使用了正确的模板。');
    }
  };

  

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const bstr = evt.target.result;
          const workbook = XLSX.read(bstr, { type: 'binary' });
          processExcelData(workbook);
        } catch (err) {
          setError('文件格式不支持或已损坏。');
        }
      };
      reader.readAsBinaryString(file);
    }
  };


  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const workbook = XLSX.read(bstr, { type: 'binary' });
        processExcelData(workbook);
      } catch (err) {
        setError('文件格式不支持或已损坏。');
      }
    };
    reader.readAsBinaryString(file);
  };

  const submitImport = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/brand-projects/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ projects: parsedData.map(p => ({ ...p, system })) })
      });
      const data = await res.json();
      if (data.success) {
        onImportSuccess();
        handleClose();
      } else {
        setError(data.error || '导入失败');
      }
    } catch (err) {
      setError('网络错误，请稍后再试');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center p-6 border-b border-gray-100 dark:border-slate-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">批量导入品牌与竞品配置</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-xl border border-blue-100 dark:border-blue-800/30">
                <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">第 1 步：下载标准模板</h3>
                <p className="text-sm text-blue-600 dark:text-blue-400 mb-4">
                  请下载标准 Excel 模板，并在对应的 Sheet 页中填入您的品牌、竞品、人设和业务维度数据。
                </p>
                <a
                  href={system === 'seo' ? "/NexSEO_Brand_Import_Template.xlsx" : "/NexGEO_Brand_Import_Template.xlsx"}
                  download
                  className="inline-flex items-center space-x-2 bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 px-4 py-2 rounded-lg border border-blue-200 dark:border-slate-600 hover:bg-blue-50 dark:hover:bg-slate-600 transition-colors"
                >
                  <Download size={16} />
                  <span>下载标准导入模板.xlsx</span>
                </a>
              </div>

              <div className="bg-gray-50 dark:bg-slate-800/50 p-5 rounded-xl border border-gray-200 dark:border-slate-700">
                <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">第 2 步：上传填好的表格</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  支持增量导入，系统会根据您填写的“归属品牌名称”自动为您创建多个监控项目。同名品牌将被允许独立创建。
                </p>
                
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className={`w-full flex flex-col items-center justify-center py-10 border-2 border-dashed rounded-xl transition-colors cursor-pointer group ${isDragOver ? "bg-indigo-50 border-indigo-500 dark:bg-indigo-900/30 dark:border-indigo-400" : "border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700/50 hover:border-indigo-400"}`}
                >
                  <div className="bg-indigo-100 dark:bg-indigo-900/30 p-3 rounded-full mb-3 group-hover:scale-110 transition-transform">
                    <Upload className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">点击或拖拽上传 Excel 文件</span>
                  <span className="text-sm text-gray-400 mt-1">仅支持 .xlsx 格式</span>
                </button>
              </div>
            </div>
          )}

          {step === 2 && summary && (
            <div className="space-y-6">
              <div className="flex items-center justify-center space-x-3 text-green-600 dark:text-green-400 mb-6">
                <CheckCircle size={32} />
                <h3 className="text-xl font-bold">解析成功</h3>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-xl text-center">
                  <div className="text-sm text-indigo-600 dark:text-indigo-400 mb-1">待创建品牌</div>
                  <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">{summary.brands}</div>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-xl text-center">
                  <div className="text-sm text-emerald-600 dark:text-emerald-400 mb-1">竞品数量</div>
                  <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{summary.competitors}</div>
                </div>
                
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl text-center">
                  <div className="text-sm text-blue-600 dark:text-blue-400 mb-1">{system === 'seo' ? '关键词' : '受众人设'}</div>
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{system === 'seo' ? summary.seoKeywords : summary.personas}</div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-xl text-center">
                  <div className="text-sm text-purple-600 dark:text-purple-400 mb-1">{system === 'seo' ? '分组数量' : '业务维度'}</div>
                  <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">{system === 'seo' ? summary.seoGroups : summary.themes}</div>
                  </div>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg text-sm text-yellow-800 dark:text-yellow-300">
                <p className="font-semibold mb-1">温馨提示：</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>本次导入的品牌默认仅对您和同组成员可见，如需协作人请在导入后单独配置。</li>
                  <li>导入需要数秒钟，请勿关闭弹窗。</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-100 dark:border-slate-700 flex justify-end space-x-3 bg-gray-50 dark:bg-slate-800/80">
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          >
            取消
          </button>
          
          {step === 2 && (
            <button
              onClick={submitImport}
              disabled={isSubmitting}
              className="flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-6 py-2 rounded-lg transition-colors shadow-sm"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>正在导入...</span>
                </>
              ) : (
                <>
                  <Upload size={16} />
                  <span>确认导入</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
